import { Form, Formik } from 'formik'
import React, { ReactElement, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import config from '@config'
import { CheckoutFormFields } from '@enums'
import amendmentUtils from '@lib/amendment'
import bem from '@lib/bem'
import bookingUtils from '@lib/booking'
import { useBookingByIdLoader } from '@loaders/booking'
import Details from '@pages/Checkout/Amendment/Details'
import CheckoutFields from '@pages/Checkout/Fields'
import VacancyLoader from '@pages/Checkout/Form/VacancyLoader'
import { CheckoutFormData, useInitialFormValues } from '@pages/Checkout/hooks/useInitialFormValues'
import useCarrierQueries from '@queries/carriers'
import { useParams } from '@stores/params'

import '@pages/Checkout/Form/index.scss'

export interface FormContentProps {
  booking: Booking.Data | null
  isBookingLoading: boolean
  isConnectionsLoading?: boolean
}

const AVAILABLE_FIELDS: CheckoutFormFields[] = [
  CheckoutFormFields.SplitFareClasses,
  CheckoutFormFields.FareClassSelection,
  CheckoutFormFields.Seats,
]

const FormContent = ({ booking, isBookingLoading }: FormContentProps): ReactElement => {
  const [{ currency, locale, retailerPartnerNumber }] = useParams()
  const isAvoidVacancy = config.amendments.avoidVacancy.byRpn.includes(retailerPartnerNumber)

  const carrierQueries = useCarrierQueries({
    connections: [booking?.outboundConnection, booking?.inboundConnection],
    currency,
    locale,
  })

  return (
    <>
      {!isAvoidVacancy && <VacancyLoader />}
      <div className="checkout-form" data-tag="checkout-form">
        <div className="row gap-4 start column-sm">
          <div className="cell-8 cell-sm-12">
            <div className="column gap-4">
              <CheckoutFields disabled={false} supportedFields={AVAILABLE_FIELDS} />
            </div>
          </div>
          <div className="cell-4 cell-sm-12">
            <div className="column gap-4 checkout-form__trip-details">
              <div className="cell">
                <div className={bem('checkout', 'section')}>
                  <Details
                    isCarrierLoading={carrierQueries.isLoading}
                    isBookingLoading={isBookingLoading}
                    carriers={carrierQueries.data}
                    totalPrice={booking?.booking.totalPrice}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

interface AmendmentFormProps {
  isLoading?: boolean
}

const AmendmentForm = ({ isLoading }: AmendmentFormProps): ReactElement => {
  const initialValues = useInitialFormValues()
  const [params] = useParams()
  const { search } = useLocation()
  const navigate = useNavigate()

  const { data, isLoading: isBookingLoading } = useBookingByIdLoader({
    bookingId: params.bookingId as string,
    params: { locale: params.locale, retailerPartnerNumber: params.retailerPartnerNumber },
  })

  const passengers = useMemo(
    () =>
      bookingUtils
        .getSegmentPassengers(data?.outboundConnection)
        .map(p => ({ ...p.passenger, pax: 1, type: p.passenger.type })),
    [data],
  )

  const submitForm = (formData: CheckoutFormData): void => {
    amendmentUtils.setSessionStorage(params.bookingId as string, { ...formData, passengers })
    data && navigate(`/booking/${data.booking.id}/payment${search}`)
  }

  return (
    <Formik initialValues={initialValues} onSubmit={submitForm}>
      <Form>
        <FormContent booking={data} isBookingLoading={isBookingLoading} isConnectionsLoading={isLoading} />
      </Form>
    </Formik>
  )
}

export default AmendmentForm
